import { createContext, useContext, ReactNode } from 'react'
import { MovimientosProvider, useMovimientos } from './hooks/useMovimientos'

interface MovimientosFilters {
  operationFilter?: string // Tipo (ENTRY, OUT)
  almacenFilter?: string // Almacén
  userFilter?: string // Usuario (ID)
  categoryFilter?: string // Categoría
  entryDateFrom?: string // Fecha desde (YYYY-MM-DD)
  entryDateTo?: string // Fecha hasta (YYYY-MM-DD)
}

interface MovimientosTableContextType {
  currentPage: number
  movimientos: unknown[]
  isLoading: boolean
  skeletonRowCount: number
  isFetching: boolean
  isError: boolean
  refetch: () => void
  pageSize: number
  setCurrentPage: (_p: number) => void
  setPageSize: (_s: number) => void
  totalItems: number
  totalPages: number
  filters: MovimientosFilters
  setFilters: React.Dispatch<React.SetStateAction<MovimientosFilters>>
  clearFilters: () => void
}

const MovimientosTableContext = createContext<
  MovimientosTableContextType | undefined
>(undefined)

interface MovimientosTableContextProviderProps {
  children: ReactNode
  loteId: string
}

function MovimientosTableContextInner({ children }: { children: ReactNode }) {
  const {
    movimientos,
    isLoading,
    isFetching,
    isError,
    currentPage,
    refetch,
    pageSize,
    setCurrentPage,
    setPageSize,
    totalItems,
    totalPages,
    filters,
    setFilters,
    clearFilters,
  } = useMovimientos()

  const skeletonRowCount = 10

  return (
    <MovimientosTableContext.Provider
      value={{
        currentPage,
        movimientos,
        isLoading,
        skeletonRowCount,
        isFetching,
        isError,
        refetch,
        pageSize,
        setCurrentPage,
        setPageSize,
        totalItems,
        totalPages,
        filters,
        setFilters,
        clearFilters,
      }}
    >
      {children}
    </MovimientosTableContext.Provider>
  )
}

export function MovimientosTableContextProvider({
  children,
  loteId,
}: MovimientosTableContextProviderProps) {
  return (
    <MovimientosProvider loteId={loteId}>
      <MovimientosTableContextInner>{children}</MovimientosTableContextInner>
    </MovimientosProvider>
  )
}

export function useMovimientosTableContext() {
  const context = useContext(MovimientosTableContext)
  if (context === undefined) {
    throw new Error(
      'useMovimientosTableContext must be used within a MovimientosTableContextProvider'
    )
  }
  return context
}
